// truck-dialog.component.ts

import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogActions,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DatePipe } from '@angular/common';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { TemplateDialogComponent } from '@app/shared/template-dialog/template-dialog.component';
import { environment } from 'src/environments/environment';

interface Carriers {
  id: number;
  name: string;
  state: string;
}

interface LocationOption {
  id?: number;
  city: string;
  state: string;
  zip?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  country?: string | null;
  timezone?: string | null;
}

@Component({
  selector: 'app-truck-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,

    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,

    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatIconModule,
  ],
  templateUrl: './truck-dialog.component.html',
  styleUrl: './truck-dialog.component.scss',
  providers: [provideNativeDateAdapter(), DatePipe],
})
export class TruckDialogComponent {

  testing: boolean = false;

  form!: FormGroup;
  templateForm!: FormGroup;

  truck: any | null = null;
  mode: 'add' | 'edit' = 'add';
  copy = false;

  originOptions: LocationOption[] = [];
  filteredOriginOptions: LocationOption[] = [];

  loadingOrigin = signal(false);

  minSearchLength = 5;

  @ViewChild('originInput') originInput!: ElementRef<HTMLInputElement>;
  @ViewChild('originInput', { read: MatAutocompleteTrigger })
  originTrigger!: MatAutocompleteTrigger;

  carriers: Carriers[] = [];
  templates: any[] = [];

  today = this.startOfDay(new Date());

  constructor(
    public dialogRef: MatDialogRef<TruckDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthService,
    private appService: AppService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private datePipe: DatePipe
  ) {
    this.truck = data?.truck ?? null;
    this.mode = data?.mode ?? 'add';
    this.copy = data?.copy ?? false;

    const tomorrow = this.addDays(this.today, 1);

    this.form = this.fb.group({
      
      available_start_date: new FormControl<Date | null>(
        this.today,
        [Validators.required, this.notPastDateValidator()]
      ),
      available_end_date: new FormControl<Date | null>(
        tomorrow,
        [Validators.required, this.notPastDateValidator()]
      ),

      origin: [null, Validators.required],
      origin_city: [null, Validators.required],
      origin_state: [null, Validators.required],
      origin_zip: [null, Validators.required],
      origin_latitude: [null, Validators.required],
      origin_longitude: [null, Validators.required],
      origin_country: [null, Validators.required],

      carrier_id: [null, Validators.required],
      carrier_name: [null, Validators.required],
      carrier_base_state: [null, Validators.required],

      axle_count: [null, [Validators.required, Validators.min(5)]],
      max_weight: [null, Validators.required],
      well_length: [null, Validators.required],

      min_rate: [null],
      min_rpm: [null],
      min_distance: [null],

      reference: [null],
      note: [null],

      team: false,
      tarp: false,
      hazmat: false,
      military_base_access: false,
      twic: false,
      emodal: false,
      outriggers: false,

      visible: true,
    });

    this.templateForm = this.fb.group({
      id: [null as number | null],
    });
  }

  ngOnInit(): void {
    if (!this.authService.userCanPostTrucks()) {
      this.dialogRef.close();
      return;
    }

    this.getCarriersOptions();
    this.getTemplates();

    if (this.truck) {
      this.patchForm(this.truck);
    }
  }

  private notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      const selected = this.startOfDay(new Date(value));

      return selected < this.today ? { pastDate: true } : null;
    };
  }

  filterOrigin(): void {
    const value = this.originInput.nativeElement.value.toLowerCase();

    if (value.length < this.minSearchLength) {
      this.filteredOriginOptions = [];
      return;
    }

    const alreadyInOptions = this.originOptions.some(
      (opt) => this.formatLocation(opt).toLowerCase() === value
    );

    if (!alreadyInOptions) {
      this.updateOriginOptions(value);
    }

    const matches = this.originOptions.filter((opt) =>
      this.formatLocation(opt).toLowerCase().includes(value)
    );

    this.filteredOriginOptions = matches;
  }

  updateOriginOptions(search: string): void {
    this.loadingOrigin.set(true);

    this.appService.getLocations(search).subscribe({
      next: (res) => {
        const data = Array.isArray(res.data) ? res.data : [];

        this.originOptions = data;
        this.filteredOriginOptions = data;

        this.loadingOrigin.set(false);

        setTimeout(() => this.originTrigger.openPanel());
      },
      error: (err) => {
        console.error('Error:', err);

        this.originOptions = [];
        this.filteredOriginOptions = [];

        this.loadingOrigin.set(false);
      },
    });
  }

  formatLocation(item: LocationOption | null): string {
    if (!item) return '';

    return `${item.city}, ${item.state}${item?.zip ? ` ${item.zip}` : ''}`;
  }

  onOriginSelected(item: LocationOption): void {
    if (!item) return;

    this.form.patchValue({
      origin: this.formatLocation(item),
      origin_city: item.city ?? null,
      origin_state: item.state ?? null,
      origin_zip: item.zip ?? null,
      origin_latitude: item.latitude ?? null,
      origin_longitude: item.longitude ?? null,
      origin_country: item.country ?? 'USA',
      origin_timezone: item.timezone ?? null,
    });
  }

  onSubmit(): void {

    if (this.form.invalid) {

      if (this.testing) {
        this.logInvalidFields(this.form);
      }

      this.form.markAllAsTouched();

      this.appService.showSnackBar(
        'Please fill in all required fields.',
        'error'
      );

      return;
    }

    const formattedData = this.formatFormDates(this.form);

    formattedData.well_length = formattedData.well_length
      ? Math.round(Number(formattedData.well_length) * 12)
      : null;

    if (this.mode === 'add') {
      this.appService.post('trucks', formattedData).subscribe({
        next: (success) => {
          this.appService.showSnackBar(success.message, 'success');
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Error:', error);
          this.appService.handleError(error);
        },
      });
    }

    if (this.mode === 'edit' && this.truck) {
      this.appService.patch(`trucks/${this.truck.id}`, formattedData).subscribe({
        next: (success) => {
          this.appService.showSnackBar(success.message, 'success');
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Error:', error);
          this.appService.handleError(error);
        },
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  deleteTruck(): void {
    if (!this.truck) return;

    this.appService
      .confirm('Are you sure you want to delete this truck?')
      .subscribe((result) => {
        if (!result) return;

        this.appService.delete(`trucks/${this.truck.id}`).subscribe({
          next: (response) => {
            this.appService.showSnackBar(response.message, 'success');
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.appService.handleError(err);
            console.error('Error deleting truck:', err);
          },
        });
      });
  }

  private formatFormDates(form: FormGroup): any {
    const formatted = { ...form.getRawValue() };

    if (formatted.available_start_date) {
      formatted.available_start_date = this.datePipe.transform(
        formatted.available_start_date,
        'yyyy-MM-dd'
      );
    }

    if (formatted.available_end_date) {
      formatted.available_end_date = this.datePipe.transform(
        formatted.available_end_date,
        'yyyy-MM-dd'
      );
    }

    return formatted;
  }

  patchForm(truck: any): void {
    let available_start_date = this.parseDateLocal(truck.available_start_date);
    let available_end_date = this.parseDateLocal(truck.available_end_date);

    if (this.copy) {
      available_start_date = this.today;
      available_end_date = this.addDays(this.today, 1);
      this.mode = 'add';
    }

    this.form.patchValue({
      ...truck,
      well_length: truck.well_length != null
        ? Number((Number(truck.well_length) / 12).toFixed(1))
        : null,
      available_start_date,
      available_end_date,
    });

    if (this.mode === 'edit') {
      if (!environment.allowTruckOriginLocationChange) {
        this.form.get('origin')?.disable();
      }
    }
  }

  getCarriersOptions(): void {
    const user = this.authService.getUserFromStorage();

    if (!user || !Array.isArray(user.carriers)) {
      console.error('User not found or no carriers');
      return;
    }

    this.carriers = user.carriers
      .filter(
        (conn: any) =>
          conn.allowed_to_operate === 'Y' &&
          (conn.carrier_common_authority_status === 'A' ||
            conn.carrier_contract_authority_status === 'A')
      )
      .map((conn: any) => ({
        id: conn.id,
        name: conn.dba_name?.trim() || conn.legal_name,
        state: conn.phy_state?.trim() || null,
      }));

    if (this.carriers.length === 1 && this.mode === 'add') {
      this.form.patchValue({
        carrier_id: this.carriers[0].id,
        carrier_name: this.carriers[0].name,
        carrier_base_state: this.carriers[0].state,
      });
    }
  }

  onCarrierChange(event: any): void {
    const selectedCarrier = this.carriers.find((c) => c.id === event.value);

    if (selectedCarrier) {
      this.form.patchValue({
        carrier_id: selectedCarrier.id,
        carrier_name: selectedCarrier.name,
        carrier_base_state: selectedCarrier.state,
      });
    } else {
      this.form.patchValue({
        carrier_id: null,
        carrier_name: null,
        carrier_base_state: null,
      });
    }
  }

  toFeetAndInches(inches: number): string {
    if (!inches || inches <= 0) return '0 in';

    const feet = Math.floor(inches / 12);
    const remainder = inches % 12;

    return `${feet} ft ${remainder} in`;
  }

  getTemplates(): void {
    this.appService.getMyTruckTemplates().subscribe({
      next: (res) => {
        this.templates = res.data;
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  addTemplate(mode: 'copy' | 'create' = 'create'): void {
    if (mode === 'copy' && !this.templateForm.get('id')?.value) {
      console.error('No template selected for copying');
      return;
    }

    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    const formValue = this.getCleanFormValue(this.form);

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode,
        name: selectedTemplate?.name ?? null,
        type: 'truck',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        save_origin: result.save_origin,
        payload: formValue,
      };

      this.appService.post('trucks/templates', payload).subscribe({
        next: (response) => {
          this.appService.showSnackBar(response.message, 'success');

          const templateId = response.data.id;

          this.getTemplates();
          this.templateForm.get('id')?.setValue(templateId);
        },
        error: (err) => this.appService.handleError(err),
      });
    });
  }

  updateTemplate(): void {
    const formValue = this.getCleanFormValue(this.form);

    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    if (!selectedTemplate) {
      console.error('No template selected for update');
      return;
    }

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode: 'update',
        name: selectedTemplate?.name ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        save_origin: result.save_origin,
        payload: formValue,
      };

      this.appService.patch(`trucks/templates/${selectedTemplate.id}`, payload).subscribe({
        next: (response) => {
          this.appService.showSnackBar(response.message, 'success');
          this.getTemplates();
        },
        error: (err) => this.appService.handleError(err),
      });
    });
  }

  deleteTemplate(): void {
    if (!this.templateForm.get('id')?.value) {
      console.error('No template selected for deletion');
      return;
    }

    this.appService
      .confirm('Are you sure you want to delete this template?')
      .subscribe((ok) => {
        if (!ok) return;

        const id = this.templateForm.value.id;
        if (!id) return;

        this.appService.delete(`trucks/templates/${id}`).subscribe({
          next: (response) => {
            this.appService.showSnackBar(response.message, 'success');
            this.clearForm();
            this.getTemplates();
            this.templateForm.reset();
            this.onTemplateChange();
          },
          error: (err) => this.appService.handleError(err),
        });
      });
  }

  onTemplateChange(): void {
    const id = this.templateForm.value.id as number | null;
    const selected = this.templates.find((t: any) => t.id === id);

    if (!selected) {
      this.clearForm();
      return;
    }

    this.patchTemplate(selected.payload ?? {});
  }

  private patchTemplate(payload: any): void {
    const p = payload ?? {};
    const out: any = { ...p };

    if (typeof p.pickup_window_days === 'number') {
      out.available_start_date = this.today;
      out.available_end_date = this.addDays(this.today, p.pickup_window_days);
    }

    delete out.pickup_window_days;

    this.clearForm();
    this.form.patchValue(out);
  }

  private clearForm(preserveCarrier = true): void {
    const snapshot = this.form.getRawValue();

    this.form.reset();

    if (preserveCarrier) {
      this.form.patchValue(
        {
          carrier_id: snapshot.carrier_id ?? null,
          carrier_name: snapshot.carrier_name ?? null,
          carrier_base_state: snapshot.carrier_base_state ?? null,
        },
        { emitEvent: false }
      );
    }
  }

  private getCleanFormValue(form: FormGroup): any {
    const result: any = {};
    const raw: any = {};

    Object.keys(form.controls).forEach((key) => {
      const value = form.get(key)?.value;

      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        raw[key] =
          value instanceof Date
            ? this.datePipe.transform(value, 'yyyy-MM-dd')
            : value;
      }
    });

    const pickup_window_days = this.daysBetween(
      raw.available_start_date,
      raw.available_end_date
    );

    if (pickup_window_days !== undefined) {
      result.pickup_window_days = pickup_window_days;
    }

    const exclude = new Set([
      'available_start_date',
      'available_end_date',
      'carrier_id',
      'carrier_name',
      'carrier_base_state',
    ]);

    Object.keys(raw).forEach((key) => {
      if (!exclude.has(key)) {
        result[key] = raw[key];
      }
    });

    return result;
  }

  private daysBetween(a?: string | Date, b?: string | Date): number | undefined {
    const toDate = (x?: string | Date) =>
      x instanceof Date
        ? new Date(x.getFullYear(), x.getMonth(), x.getDate())
        : typeof x === 'string'
        ? new Date(x + 'T00:00:00')
        : undefined;

    const d1 = toDate(a);
    const d2 = toDate(b);

    return d1 && d2
      ? Math.round((d2.getTime() - d1.getTime()) / 86400000)
      : undefined;
  }

  private parseDateLocal(dateString?: string | null): Date | null {
    if (!dateString) return null;

    const parts = dateString.split('-').map(Number);

    if (parts.length !== 3 || parts.some(isNaN)) {
      return null;
    }

    const [year, month, day] = parts;

    return new Date(year, month - 1, day);
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private addDays(base: Date, days: number): Date {
    const d = this.startOfDay(base);
    d.setDate(d.getDate() + days);
    return d;
  }

  private logInvalidFields(form: FormGroup): void {
    const invalidFields: Record<string, any> = {};

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control?.invalid) {
        invalidFields[key] = {
          value: control.value,
          errors: control.errors,
        };
      }
    });

    console.table(invalidFields);
  }

  toFeetAndInchesFromFeet(value: number | string | null): string {
    const feetValue = Number(value);

    if (!feetValue || feetValue <= 0) {
      return '';
    }

    const totalInches = Math.round(feetValue * 12);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet} ft ${inches} in`;
  }
}